import { useTokenManager } from '@webapp/auth/TokenManager'
import { Environment } from '@webapp/util/Environment'

export interface GameCardDetail { type: string; date?: string; value?: string; place?: string; flag?: string; highlight?: boolean }
export interface GameCardImage {
  imageId: string
  caption?: string
  focusX?: number
  focusY?: number
  zoom?: number
  // A legacy blob path (e.g. "images/Foo.jpg") on cards ported from the old model. The designer heals
  // such an image by re-uploading its content, swapping in the server-assigned id, and dropping the
  // path -- so the imageId that ships with a ported card is only a placeholder until then.
  path?: string
}
export interface GameCardInfo {
  id: string; sourceType?: string; sourcePersonId?: string
  name?: string; childOrder?: number; siblingsCount?: number; childCount?: number
  flag?: string; notes?: string; text?: string; relationship?: string
  images?: GameCardImage[]; details?: GameCardDetail[]
}
export interface GameDefinition { id: string; gameType?: string; title?: string; subtitle?: string; cards?: string[] }

export function useGamesApi() {
  const tokenManager = useTokenManager()
  const root = `${Environment.get('RESEARCH_DATA_URL').replace(/\/$/, '')}/games`
  // The public gamedefinitions container (its own storage account) -- read without a token.
  const gameDefinitionsUrl = Environment.get('GAME_DEFINITIONS_URL').replace(/\/$/, '')

  async function authHeaders(extra?: Record<string, string>) {
    const token = await tokenManager.getAccessTokenAsync()
    const headers: Record<string, string> = { ...extra }
    if (token) {
      headers.Authorization = 'Bearer ' + token
    }
    return headers
  }

  async function send(method: string, path: string, body?: BodyInit, headers?: Record<string, string>) {
    return await fetch(path, {
      method,
      headers: await authHeaders(headers),
      body,
    })
  }

  function fail(method: string, path: string, status: number): never {
    throw new Error(`${method} ${path} failed: ${status}`)
  }

  const jsonHeaders = { 'Content-Type': 'application/json' }

  async function createGame(): Promise<GameDefinition> {
    const path = root
    const response = await send('POST', path)
    if (!response.ok) {
      fail('POST', path, response.status)
    }
    return await response.json() as GameDefinition
  }

  async function getDefinition(gameId: string): Promise<GameDefinition | undefined> {
    const path = `${root}/${gameId}/definition`
    const response = await send('GET', path)
    if (response.status === 404) {
      return undefined
    }
    if (!response.ok) {
      fail('GET', path, response.status)
    }
    return await response.json() as GameDefinition
  }

  async function putDefinition(gameId: string, definition: GameDefinition): Promise<void> {
    const path = `${root}/${gameId}/definition`
    const response = await send('PUT', path, JSON.stringify(definition), jsonHeaders)
    if (!response.ok) {
      fail('PUT', path, response.status)
    }
  }

  async function deleteDefinition(gameId: string): Promise<void> {
    const path = `${root}/${gameId}/definition`
    const response = await send('DELETE', path)
    if (!response.ok) {
      fail('DELETE', path, response.status)
    }
  }

  async function listCardInfo(gameId: string): Promise<GameCardInfo[]> {
    const path = `${root}/${gameId}/cardinfo`
    const response = await send('GET', path)
    if (!response.ok) {
      fail('GET', path, response.status)
    }
    return await response.json() as GameCardInfo[]
  }

  async function getCardInfo(gameId: string, cardId: string): Promise<GameCardInfo | undefined> {
    const path = `${root}/${gameId}/cardinfo/${cardId}`
    const response = await send('GET', path)
    if (response.status === 404) {
      return undefined
    }
    if (!response.ok) {
      fail('GET', path, response.status)
    }
    return await response.json() as GameCardInfo
  }

  // Card IDs are server-assigned -- POST a card without an ID and the service returns it with its ID.
  async function addCardInfo(gameId: string, card: Omit<GameCardInfo, 'id'>): Promise<GameCardInfo> {
    const path = `${root}/${gameId}/cardinfo`
    const response = await send('POST', path, JSON.stringify(card), jsonHeaders)
    if (!response.ok) {
      fail('POST', path, response.status)
    }
    return await response.json() as GameCardInfo
  }

  async function putCardInfo(gameId: string, cardId: string, card: GameCardInfo): Promise<void> {
    const path = `${root}/${gameId}/cardinfo/${cardId}`
    const response = await send('PUT', path, JSON.stringify(card), jsonHeaders)
    if (!response.ok) {
      fail('PUT', path, response.status)
    }
  }

  async function deleteCardInfo(gameId: string, cardId: string): Promise<void> {
    const path = `${root}/${gameId}/cardinfo/${cardId}`
    const response = await send('DELETE', path)
    if (!response.ok) {
      fail('DELETE', path, response.status)
    }
  }

  // Image IDs are server-assigned -- POST the raw JPEG bytes and the service returns { imageId }.
  // The optional filename rides in Content-Disposition (stored as blob metadata) -- the service
  // rejects non-ASCII, control chars, and path separators, so sanitize to a safe download name.
  async function addImage(gameId: string, data: Blob, filename?: string): Promise<{ imageId: string }> {
    const path = `${root}/${gameId}/images`
    const headers: Record<string, string> = { 'Content-Type': 'image/jpeg' }
    const safeName = filename?.replace(/[^\x20-\x7E]/g, '').replace(/["\\/]/g, '').slice(0, 255)
    if (safeName) {
      headers['Content-Disposition'] = `attachment; filename="${safeName}"`
    }
    const response = await send('POST', path, data, headers)
    if (!response.ok) {
      fail('POST', path, response.status)
    }
    return await response.json() as { imageId: string }
  }

  async function deleteImage(gameId: string, imageId: string): Promise<void> {
    const path = `${root}/${gameId}/images/${imageId}`
    const response = await send('DELETE', path)
    if (!response.ok) {
      fail('DELETE', path, response.status)
    }
  }

  // The public blob display URL (no token), served from the gamedefinitions container -- mirrors
  // MatchingGame.vue's construction. imagePathUrl is the same for a legacy path-named image.
  function imageUrl(gameId: string, imageId: string): string {
    return `${gameDefinitionsUrl}/${gameId}/images/${imageId}.jpg`
  }

  function imagePathUrl(gameId: string, imagePath: string): string {
    return `${gameDefinitionsUrl}/${gameId}/${encodeURI(imagePath)}`
  }

  return {
    createGame,
    getDefinition,
    putDefinition,
    deleteDefinition,
    listCardInfo,
    getCardInfo,
    addCardInfo,
    putCardInfo,
    deleteCardInfo,
    addImage,
    deleteImage,
    imageUrl,
    imagePathUrl,
  }
}
